import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  PlusIcon, 
  MagnifyingGlassIcon, 
  FunnelIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  DocumentArrowDownIcon,
  UserIcon,
  BuildingOfficeIcon,
  PhoneIcon,
  MapPinIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { Button, Input, Card, CardContent, CardHeader, CardTitle, Modal, Badge, Pagination } from '../../components/ui';
import { ClientDetailModal } from '../../components/ClientDetailModal';
import { clientService } from '../../services/clientService';
import { formatDate } from '../../utils/formatters';
import { CLIENT_TYPES, CLIENT_STATUS, SUCCESS_MESSAGES } from '../../constants';

export const ClientsPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    type: ''
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // Form states
  const [createForm, setCreateForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    phone_secondary: '',
    address: '',
    city: '',
    state: '',
    country: 'México',
    postal_code: '',
    client_type: 'individual',
    status: 'active',
    contact_person: '',
    contact_position: '',
    tax_id: '',
    website: '',
    notes: ''
  });
  const [createFormErrors, setCreateFormErrors] = useState({});

  // Edit form states
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    phone_secondary: '',
    address: '',
    city: '',
    state: '',
    country: 'México',
    postal_code: '',
    client_type: 'individual',
    status: 'active',
    contact_person: '',
    contact_position: '',
    tax_id: '',
    website: '',
    notes: ''
  });
  const [editFormErrors, setEditFormErrors] = useState({});

  const queryClient = useQueryClient();

  // Fetch clients
  const { data: clientsData, isLoading, error } = useQuery({
    queryKey: ['clients', searchTerm, filters],
    queryFn: () => clientService.getClients({ 
      search: searchTerm, 
      ...filters
    }),
  });

  // Extract clients and pagination info from response
  const allClients = clientsData?.results || clientsData || [];
  
  // Client-side pagination (temporary until backend implements it)
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const clients = allClients.slice(startIndex, endIndex);
  
  const paginationInfo = {
    count: allClients.length,
    total_pages: Math.ceil(allClients.length / itemsPerPage),
    current_page: currentPage,
    page_size: itemsPerPage
  };

  // Create client mutation
  const createClientMutation = useMutation({
    mutationFn: clientService.createClient,
    onSuccess: () => {
      queryClient.invalidateQueries(['clients']);
      setIsCreateModalOpen(false);
      resetCreateForm();
      // Show success message (you can add a toast notification here)
      console.log(SUCCESS_MESSAGES.CLIENT_CREATED);
    },
    onError: (error) => {
      console.error('Error creating client:', error);
      if (error.response?.data?.errors) {
        setCreateFormErrors(error.response.data.errors);
      }
    },
  });

  // Update client mutation
  const updateClientMutation = useMutation({
    mutationFn: ({ id, data }) => clientService.updateClient(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['clients']);
      setIsEditModalOpen(false);
      setSelectedClient(null);
      // Show success message
      console.log(SUCCESS_MESSAGES.CLIENT_UPDATED);
    },
    onError: (error) => {
      console.error('Error updating client:', error);
    },
  });

  // Delete client mutation
  const deleteClientMutation = useMutation({
    mutationFn: clientService.deleteClient,
    onSuccess: () => {
      queryClient.invalidateQueries(['clients']);
      // Show success message
      console.log(SUCCESS_MESSAGES.CLIENT_DELETED);
    },
  });

  const handleSearch = (e) => {
    handleSearchChange(e.target.value);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = {
      ...filters,
      [key]: value
    };
    handleFiltersChange(newFilters);
  };

  // Form handlers
  const handleCreateFormChange = (field, value) => {
    setCreateForm(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (createFormErrors[field]) {
      setCreateFormErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const resetCreateForm = () => {
    setCreateForm({
      name: '',
      email: '',
      company: '',
      phone: '',
      phone_secondary: '',
      address: '',
      city: '',
      state: '',
      country: 'México',
      postal_code: '',
      client_type: 'individual',
      status: 'active',
      contact_person: '',
      contact_position: '',
      tax_id: '',
      website: '',
      notes: ''
    });
    setCreateFormErrors({});
  };

  const handleCreateClient = (e) => {
    e.preventDefault();
    setCreateFormErrors({});
    
    // Preparar datos para envío
    const formData = {
      ...createForm,
      // Limpiar espacios en blanco
      name: createForm.name.trim(),
      email: createForm.email.trim(),
      company: createForm.company.trim() || 'Sin especificar',
      phone: createForm.phone.trim(),
      phone_secondary: createForm.phone_secondary.trim(),
      address: createForm.address.trim() || 'Sin especificar',
      city: createForm.city.trim() || 'Sin especificar',
      state: createForm.state.trim() || 'Sin especificar',
      country: createForm.country.trim() || 'México',
      postal_code: createForm.postal_code.trim() || '00000',
      contact_person: createForm.contact_person.trim() || 'Sin especificar',
      contact_position: createForm.contact_position.trim(),
      tax_id: createForm.tax_id.trim(),
      website: createForm.website.trim(),
      notes: createForm.notes.trim(),
    };
    
    // Validar formato de teléfono
    const phoneRegex = /^\+?1?\d{9,15}$/;
    if (!phoneRegex.test(formData.phone)) {
      setCreateFormErrors({ phone: 'El teléfono debe tener entre 9 y 15 dígitos. Ejemplo: +1234567890' });
      return;
    }
    
    // Agregar + al teléfono si no lo tiene
    if (!formData.phone.startsWith('+')) {
      formData.phone = '+' + formData.phone;
    }
    
    createClientMutation.mutate(formData);
  };

  const handleCreateModalClose = () => {
    setIsCreateModalOpen(false);
    resetCreateForm();
  };

  // Edit form handlers
  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (editFormErrors[field]) {
      setEditFormErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const resetEditForm = () => {
    setEditForm({
      name: '',
      email: '',
      company: '',
      phone: '',
      phone_secondary: '',
      address: '',
      city: '',
      state: '',
      country: 'México',
      postal_code: '',
      client_type: 'individual',
      status: 'active',
      contact_person: '',
      contact_position: '',
      tax_id: '',
      website: '',
      notes: ''
    });
    setEditFormErrors({});
  };

  const populateEditForm = (client) => {
    setEditForm({
      name: client.name || '',
      email: client.email || '',
      company: client.company || '',
      phone: client.phone || '',
      phone_secondary: client.phone_secondary || '',
      address: client.address || '',
      city: client.city || '',
      state: client.state || '',
      country: client.country || 'México',
      postal_code: client.postal_code || '',
      client_type: client.client_type || 'individual',
      status: client.status || 'active',
      contact_person: client.contact_person || '',
      contact_position: client.contact_position || '',
      tax_id: client.tax_id || '',
      website: client.website || '',
      notes: client.notes || ''
    });
  };

  const handleEditClientSubmit = (e) => {
    e.preventDefault();
    setEditFormErrors({});
    
    // Preparar datos para envío
    const formData = {
      ...editForm,
      // Limpiar espacios en blanco
      name: editForm.name.trim(),
      email: editForm.email.trim(),
      company: editForm.company.trim() || 'Sin especificar',
      phone: editForm.phone.trim(),
      phone_secondary: editForm.phone_secondary.trim(),
      address: editForm.address.trim() || 'Sin especificar',
      city: editForm.city.trim() || 'Sin especificar',
      state: editForm.state.trim() || 'Sin especificar',
      country: editForm.country.trim() || 'México',
      postal_code: editForm.postal_code.trim() || '00000',
      contact_person: editForm.contact_person.trim() || 'Sin especificar',
      contact_position: editForm.contact_position.trim(),
      tax_id: editForm.tax_id.trim(),
      website: editForm.website.trim(),
      notes: editForm.notes.trim(),
    };
    
    // Validar formato de teléfono
    const phoneRegex = /^\+?1?\d{9,15}$/;
    if (!phoneRegex.test(formData.phone)) {
      setEditFormErrors({ phone: 'El teléfono debe tener entre 9 y 15 dígitos. Ejemplo: +1234567890' });
      return;
    }
    
    // Agregar + al teléfono si no lo tiene
    if (!formData.phone.startsWith('+')) {
      formData.phone = '+' + formData.phone;
    }
    
    updateClientMutation.mutate({ id: selectedClient.id, data: formData });
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setSelectedClient(null);
    resetEditForm();
  };

  const handleDeleteClient = (clientId) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este cliente?')) {
      deleteClientMutation.mutate(clientId);
    }
  };

  const handleEditClient = (client) => {
    setSelectedClient(client);
    populateEditForm(client);
    setIsEditModalOpen(true);
  };

  const handleViewClient = (client) => {
    setSelectedClientId(client.id);
    setIsViewModalOpen(true);
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Reset pagination when filters change
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleSearchChange = (newSearchTerm) => {
    setSearchTerm(newSearchTerm);
    setCurrentPage(1); // Reset to first page when search changes
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'secondary';
      case 'suspended': return 'danger';
      case 'pending': return 'warning';
      default: return 'secondary';
    }
  };

  const getTypeLabel = (type) => {
    const typeObj = CLIENT_TYPES.find(t => t.value === type);
    return typeObj ? typeObj.label : type;
  };

  const getStatusLabel = (status) => {
    const statusObj = CLIENT_STATUS.find(s => s.value === status);
    return statusObj ? statusObj.label : status;
  };

  const getTypeBadgeVariant = (type) => {
    switch (type) {
      case 'individual': return 'info';
      case 'small_business': return 'success';
      case 'medium_business': return 'warning';
      case 'large_business': return 'danger';
      case 'enterprise': return 'primary';
      default: return 'secondary';
    }
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Hace un momento';
    if (diffInSeconds < 3600) return `Hace ${Math.floor(diffInSeconds / 60)} min`;
    if (diffInSeconds < 86400) return `Hace ${Math.floor(diffInSeconds / 3600)} h`;
    if (diffInSeconds < 2592000) return `Hace ${Math.floor(diffInSeconds / 86400)} días`;
    if (diffInSeconds < 31536000) return `Hace ${Math.floor(diffInSeconds / 2592000)} meses`;
    return `Hace ${Math.floor(diffInSeconds / 31536000)} años`;
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Clientes</h1>
        </div>
        <div className="grid gap-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-20 bg-gray-200 animate-pulse rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Clientes</h1>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <p className="text-red-600">Error al cargar los clientes</p>
              <p className="text-sm text-gray-500 mt-2">{error.message}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clientes</h1>
          <p className="text-gray-600">Gestiona la información de tus clientes</p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          <PlusIcon className="h-4 w-4 mr-2" />
          Nuevo Cliente
        </Button>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <Input
                placeholder="Buscar clientes..."
                value={searchTerm}
                onChange={handleSearch}
                icon={<MagnifyingGlassIcon className="h-4 w-4" />}
              />
            </div>
            <div>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos los estados</option>
                {CLIENT_STATUS.map(status => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <select
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos los tipos</option>
                {CLIENT_TYPES.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Clients Table */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Lista de Clientes</CardTitle>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">
                {clients ? `${clients.length} cliente${clients.length !== 1 ? 's' : ''}` : '0 clientes'}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.print()}
                className="text-gray-500 hover:text-gray-700"
              >
                <DocumentArrowDownIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {clients && clients.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left py-4 px-6 font-semibold text-gray-900">
                      Cliente
                    </th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-900">
                      Empresa
                    </th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-900">
                      Tipo
                    </th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-900">
                      Estado
                    </th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-900">
                      Contacto
                    </th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-900">
                      Licencias
                    </th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-900">
                      Registro
                    </th>
                    <th className="text-right py-4 px-6 font-semibold text-gray-900">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {clients.map((client) => (
                    <tr key={client.id} className="hover:bg-gray-50 transition-colors">
                      {/* Cliente */}
                      <td className="py-4 px-6">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <span className="text-sm font-medium text-blue-600">
                                {client.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {client.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {client.email}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Empresa */}
                      <td className="py-4 px-6">
                        <div className="text-sm text-gray-900">
                          {client.company || '-'}
                        </div>
                        {client.city && (
                          <div className="text-sm text-gray-500">
                            {client.city}
                          </div>
                        )}
                      </td>

                      {/* Tipo */}
                      <td className="py-4 px-6">
                        <Badge 
                          variant={getTypeBadgeVariant(client.client_type)}
                          className="text-xs"
                        >
                          {getTypeLabel(client.client_type)}
                        </Badge>
                      </td>

                      {/* Estado */}
                      <td className="py-4 px-6">
                        <Badge variant={getStatusBadgeVariant(client.status)}>
                          {getStatusLabel(client.status)}
                        </Badge>
                      </td>

                      {/* Contacto */}
                      <td className="py-4 px-6">
                        <div className="text-sm text-gray-900">
                          {client.phone}
                        </div>
                        {client.contact_person && (
                          <div className="text-sm text-gray-500">
                            {client.contact_person}
                          </div>
                        )}
                      </td>

                      {/* Licencias */}
                      <td className="py-4 px-6">
                        <div className="flex items-center">
                          <span className="text-sm font-medium text-gray-900">
                            {client.license_count || 0}
                          </span>
                          {client.license_count > 0 && (
                            <span className="ml-2 text-xs text-gray-500">
                              activa{(client.license_count || 0) !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Fecha de Registro */}
                      <td className="py-4 px-6">
                        <div className="text-sm text-gray-900">
                          {formatDate(client.created_at)}
                        </div>
                        <div className="text-sm text-gray-500">
                          {getTimeAgo(client.created_at)}
                        </div>
                      </td>

                      {/* Acciones */}
                      <td className="py-4 px-6">
                        <div className="flex justify-end space-x-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewClient(client)}
                            className="text-gray-400 hover:text-blue-600"
                            title="Ver detalles"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditClient(client)}
                            className="text-gray-400 hover:text-green-600"
                            title="Editar cliente"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClient(client.id)}
                            className="text-gray-400 hover:text-red-600"
                            title="Eliminar cliente"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="mx-auto h-12 w-12 text-gray-400">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No hay clientes</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || Object.values(filters).some(f => f) 
                  ? 'Intenta ajustar los filtros de búsqueda'
                  : 'Comienza agregando tu primer cliente'
                }
              </p>
              {!searchTerm && !Object.values(filters).some(f => f) && (
                <div className="mt-6">
                  <Button onClick={() => setIsCreateModalOpen(true)}>
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Nuevo Cliente
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination Controls */}
      {clients.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center justify-between">
            {/* Items per page selector */}
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-700">Mostrar:</label>
              <select
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <span className="text-sm text-gray-700">por página</span>
            </div>

            {/* Pagination component */}
            <Pagination
              currentPage={paginationInfo.current_page || currentPage}
              totalPages={paginationInfo.total_pages || 1}
              onPageChange={handlePageChange}
              totalItems={paginationInfo.count || clients.length}
              itemsPerPage={itemsPerPage}
              showInfo={true}
            />
          </div>
        </div>
      )}

      {/* Create Client Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={handleCreateModalClose}
        title="Nuevo Cliente"
        size="xl"
      >
        <form onSubmit={handleCreateClient} className="p-6">
          <div className="space-y-8">
            {/* Información Personal */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <UserIcon className="h-5 w-5 mr-2 text-blue-600" />
                Información Personal
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre Completo *
                  </label>
                  <Input
                    type="text"
                    value={createForm.name}
                    onChange={(e) => handleCreateFormChange('name', e.target.value)}
                    placeholder="Nombre completo del cliente"
                    error={createFormErrors.name}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <Input
                    type="email"
                    value={createForm.email}
                    onChange={(e) => handleCreateFormChange('email', e.target.value)}
                    placeholder="cliente@empresa.com"
                    error={createFormErrors.email}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Información Empresarial */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <BuildingOfficeIcon className="h-5 w-5 mr-2 text-green-600" />
                Información Empresarial
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Empresa *
                  </label>
                  <Input
                    type="text"
                    value={createForm.company}
                    onChange={(e) => handleCreateFormChange('company', e.target.value)}
                    placeholder="Nombre de la empresa"
                    error={createFormErrors.company}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Cliente *
                  </label>
                  <select
                    value={createForm.client_type}
                    onChange={(e) => handleCreateFormChange('client_type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    {CLIENT_TYPES.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  {createFormErrors.client_type && (
                    <p className="text-red-500 text-sm mt-1">{createFormErrors.client_type}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    RFC/CUIT
                  </label>
                  <Input
                    type="text"
                    value={createForm.tax_id}
                    onChange={(e) => handleCreateFormChange('tax_id', e.target.value)}
                    placeholder="RFC o CUIT de la empresa"
                    error={createFormErrors.tax_id}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sitio Web
                  </label>
                  <Input
                    type="url"
                    value={createForm.website}
                    onChange={(e) => handleCreateFormChange('website', e.target.value)}
                    placeholder="https://www.empresa.com"
                    error={createFormErrors.website}
                  />
                </div>
              </div>
            </div>

            {/* Información de Contacto */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <PhoneIcon className="h-5 w-5 mr-2 text-purple-600" />
                Información de Contacto
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Teléfono Principal *
                  </label>
                  <Input
                    type="tel"
                    value={createForm.phone}
                    onChange={(e) => handleCreateFormChange('phone', e.target.value)}
                    placeholder="+1234567890 (9-15 dígitos)"
                    error={createFormErrors.phone}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Teléfono Secundario
                  </label>
                  <Input
                    type="tel"
                    value={createForm.phone_secondary}
                    onChange={(e) => handleCreateFormChange('phone_secondary', e.target.value)}
                    placeholder="+1234567890 (opcional)"
                    error={createFormErrors.phone_secondary}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Persona de Contacto
                  </label>
                  <Input
                    type="text"
                    value={createForm.contact_person}
                    onChange={(e) => handleCreateFormChange('contact_person', e.target.value)}
                    placeholder="Nombre de la persona de contacto"
                    error={createFormErrors.contact_person}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cargo de Contacto
                  </label>
                  <Input
                    type="text"
                    value={createForm.contact_position}
                    onChange={(e) => handleCreateFormChange('contact_position', e.target.value)}
                    placeholder="Cargo o posición"
                    error={createFormErrors.contact_position}
                  />
                </div>
              </div>
            </div>

            {/* Información de Ubicación */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <MapPinIcon className="h-5 w-5 mr-2 text-orange-600" />
                Información de Ubicación
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dirección *
                  </label>
                  <Input
                    type="text"
                    value={createForm.address}
                    onChange={(e) => handleCreateFormChange('address', e.target.value)}
                    placeholder="Dirección completa"
                    error={createFormErrors.address}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ciudad
                  </label>
                  <Input
                    type="text"
                    value={createForm.city}
                    onChange={(e) => handleCreateFormChange('city', e.target.value)}
                    placeholder="Ciudad"
                    error={createFormErrors.city}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estado/Provincia
                  </label>
                  <Input
                    type="text"
                    value={createForm.state}
                    onChange={(e) => handleCreateFormChange('state', e.target.value)}
                    placeholder="Estado o provincia"
                    error={createFormErrors.state}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    País
                  </label>
                  <Input
                    type="text"
                    value={createForm.country}
                    onChange={(e) => handleCreateFormChange('country', e.target.value)}
                    placeholder="País"
                    error={createFormErrors.country}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Código Postal
                  </label>
                  <Input
                    type="text"
                    value={createForm.postal_code}
                    onChange={(e) => handleCreateFormChange('postal_code', e.target.value)}
                    placeholder="Código postal"
                    error={createFormErrors.postal_code}
                  />
                </div>
              </div>
            </div>

            {/* Configuración y Notas */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <DocumentTextIcon className="h-5 w-5 mr-2 text-indigo-600" />
                Configuración y Notas
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estado *
                  </label>
                  <select
                    value={createForm.status}
                    onChange={(e) => handleCreateFormChange('status', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    {CLIENT_STATUS.map(status => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                  {createFormErrors.status && (
                    <p className="text-red-500 text-sm mt-1">{createFormErrors.status}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notas Adicionales
                  </label>
                  <textarea
                    value={createForm.notes}
                    onChange={(e) => handleCreateFormChange('notes', e.target.value)}
                    placeholder="Notas adicionales sobre el cliente..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="ghost"
              onClick={handleCreateModalClose}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={createClientMutation.isPending}
            >
              {createClientMutation.isPending ? 'Creando...' : 'Crear Cliente'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Client Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={handleEditModalClose}
        title="Editar Cliente"
        size="xl"
      >
        <form onSubmit={handleEditClientSubmit} className="p-6">
          <div className="space-y-8">
            {/* Información Personal */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <UserIcon className="h-5 w-5 mr-2 text-blue-600" />
                Información Personal
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre Completo *
                  </label>
                  <Input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => handleEditFormChange('name', e.target.value)}
                    placeholder="Nombre completo del cliente"
                    error={editFormErrors.name}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <Input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => handleEditFormChange('email', e.target.value)}
                    placeholder="cliente@empresa.com"
                    error={editFormErrors.email}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Información Empresarial */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <BuildingOfficeIcon className="h-5 w-5 mr-2 text-green-600" />
                Información Empresarial
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Empresa *
                  </label>
                  <Input
                    type="text"
                    value={editForm.company}
                    onChange={(e) => handleEditFormChange('company', e.target.value)}
                    placeholder="Nombre de la empresa"
                    error={editFormErrors.company}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Cliente *
                  </label>
                  <select
                    value={editForm.client_type}
                    onChange={(e) => handleEditFormChange('client_type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    {CLIENT_TYPES.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  {editFormErrors.client_type && (
                    <p className="text-red-500 text-sm mt-1">{editFormErrors.client_type}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    RFC/CUIT
                  </label>
                  <Input
                    type="text"
                    value={editForm.tax_id}
                    onChange={(e) => handleEditFormChange('tax_id', e.target.value)}
                    placeholder="RFC o CUIT de la empresa"
                    error={editFormErrors.tax_id}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sitio Web
                  </label>
                  <Input
                    type="url"
                    value={editForm.website}
                    onChange={(e) => handleEditFormChange('website', e.target.value)}
                    placeholder="https://www.empresa.com"
                    error={editFormErrors.website}
                  />
                </div>
              </div>
            </div>

            {/* Información de Contacto */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <PhoneIcon className="h-5 w-5 mr-2 text-purple-600" />
                Información de Contacto
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Teléfono Principal *
                  </label>
                  <Input
                    type="tel"
                    value={editForm.phone}
                    onChange={(e) => handleEditFormChange('phone', e.target.value)}
                    placeholder="+1234567890 (9-15 dígitos)"
                    error={editFormErrors.phone}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Teléfono Secundario
                  </label>
                  <Input
                    type="tel"
                    value={editForm.phone_secondary}
                    onChange={(e) => handleEditFormChange('phone_secondary', e.target.value)}
                    placeholder="+1234567890 (opcional)"
                    error={editFormErrors.phone_secondary}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Persona de Contacto
                  </label>
                  <Input
                    type="text"
                    value={editForm.contact_person}
                    onChange={(e) => handleEditFormChange('contact_person', e.target.value)}
                    placeholder="Nombre de la persona de contacto"
                    error={editFormErrors.contact_person}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cargo de Contacto
                  </label>
                  <Input
                    type="text"
                    value={editForm.contact_position}
                    onChange={(e) => handleEditFormChange('contact_position', e.target.value)}
                    placeholder="Cargo o posición"
                    error={editFormErrors.contact_position}
                  />
                </div>
              </div>
            </div>

            {/* Información de Ubicación */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <MapPinIcon className="h-5 w-5 mr-2 text-orange-600" />
                Información de Ubicación
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dirección *
                  </label>
                  <Input
                    type="text"
                    value={editForm.address}
                    onChange={(e) => handleEditFormChange('address', e.target.value)}
                    placeholder="Dirección completa"
                    error={editFormErrors.address}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ciudad
                  </label>
                  <Input
                    type="text"
                    value={editForm.city}
                    onChange={(e) => handleEditFormChange('city', e.target.value)}
                    placeholder="Ciudad"
                    error={editFormErrors.city}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estado/Provincia
                  </label>
                  <Input
                    type="text"
                    value={editForm.state}
                    onChange={(e) => handleEditFormChange('state', e.target.value)}
                    placeholder="Estado o provincia"
                    error={editFormErrors.state}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    País
                  </label>
                  <Input
                    type="text"
                    value={editForm.country}
                    onChange={(e) => handleEditFormChange('country', e.target.value)}
                    placeholder="País"
                    error={editFormErrors.country}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Código Postal
                  </label>
                  <Input
                    type="text"
                    value={editForm.postal_code}
                    onChange={(e) => handleEditFormChange('postal_code', e.target.value)}
                    placeholder="Código postal"
                    error={editFormErrors.postal_code}
                  />
                </div>
              </div>
            </div>

            {/* Configuración y Notas */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <DocumentTextIcon className="h-5 w-5 mr-2 text-indigo-600" />
                Configuración y Notas
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estado *
                  </label>
                  <select
                    value={editForm.status}
                    onChange={(e) => handleEditFormChange('status', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    {CLIENT_STATUS.map(status => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                  {editFormErrors.status && (
                    <p className="text-red-500 text-sm mt-1">{editFormErrors.status}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notas Adicionales
                  </label>
                  <textarea
                    value={editForm.notes}
                    onChange={(e) => handleEditFormChange('notes', e.target.value)}
                    placeholder="Notas adicionales sobre el cliente..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="ghost"
              onClick={handleEditModalClose}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={updateClientMutation.isPending}
            >
              {updateClientMutation.isPending ? 'Actualizando...' : 'Actualizar Cliente'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Client Detail Modal */}
      <ClientDetailModal
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setSelectedClientId(null);
        }}
        clientId={selectedClientId}
      />
    </div>
  );
};
